import React from 'react';
import { Card, Stack, Group, Text, Avatar, NumberInput, ActionIcon, Select } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { GroupMember } from '../../../types/group';
import trashIcon from '../../../assets/icons/trash.svg';

interface AdditionalPayersControlProps {
    form: UseFormReturnType<any>;
    members: GroupMember[];
}

interface AdditionalPayer {
    amount: number;
}

export const AdditionalPayersControl = ({ form, members }: AdditionalPayersControlProps) => {
    const additionalPayers = form.values.additional_payers || {};
    const primaryPayerId = form.values.paid_by_id;

    // Add an additional payer
    const handleAddAdditionalPayer = (memberId: string, amount: number) => {
        const newPayers = {
            ...form.values.additional_payers,
            [memberId]: { amount }
        };

        form.setFieldValue('additional_payers', newPayers);
    };

    // Remove an additional payer
    const handleRemoveAdditionalPayer = (memberId: string) => {
        if (!form.values.additional_payers) return;

        const newPayers = { ...form.values.additional_payers };
        delete newPayers[memberId];

        // If no more additional payers, set to null
        if (Object.keys(newPayers).length === 0) {
            form.setFieldValue('additional_payers', null);
        } else {
            form.setFieldValue('additional_payers', newPayers);
        }
    };

    // Get members available to add as additional payers
    const getAvailableMembers = () => {
        return members
            .filter(m =>
                (m.status === 'active' || m.status === 'ghost') &&
                m.id.toString() !== primaryPayerId &&
                !additionalPayers[m.id.toString()]
            )
            .map(member => ({
                value: member.id.toString(),
                label: member.display_name || member.email,
            }));
    };

    return (
        <Card withBorder shadow="sm" padding="md" radius="md">
            <Text fw={600} size="sm" mb="sm">Additional Payers</Text>

            {Object.keys(additionalPayers).length > 0 ? (
                <Stack>
                    {Object.entries(additionalPayers).map(([memberId, payerData]) => {
                        const member = members.find(m => m.id.toString() === memberId);
                        const payer = payerData as AdditionalPayer;

                        if (!member) return null;

                        return (
                            <Group key={memberId} justify="space-between">
                                <Group>
                                    <Avatar
                                        src={member.avatar_url}
                                        radius="xl"
                                        size="sm"
                                    >
                                        {member.display_name ? member.display_name.charAt(0) : member.email.charAt(0)}
                                    </Avatar>
                                    <Text size="sm">{member.display_name || member.email}</Text>
                                </Group>

                                <Group>
                                    <NumberInput
                                        value={payer.amount}
                                        onChange={(value) => handleAddAdditionalPayer(memberId, Number(value) || 0)}
                                        min={0.01}
                                        step={0.01}
                                        style={{ width: '100px' }}
                                    />
                                    <ActionIcon
                                        color="red"
                                        variant="subtle"
                                        onClick={() => handleRemoveAdditionalPayer(memberId)}
                                    >
                                        <img src={trashIcon} alt="Remove" width={16} />
                                    </ActionIcon>
                                </Group>
                            </Group>
                        );
                    })}
                </Stack>
            ) : (
                <Text size="sm" c="dimmed" mb="md">No additional payers added yet.</Text>
            )}

            {getAvailableMembers().length > 0 && (
                <Group mt="md">
                    <Select
                        placeholder="Add a payer"
                        data={getAvailableMembers()}
                        value={null}
                        onChange={(value) => {
                            if (value) {
                                handleAddAdditionalPayer(value, 0);
                            }
                        }}
                        style={{ flex: 1 }}
                    />
                </Group>
            )}
        </Card>
    );
}; 